"use client";
import { Heading } from "@cmp-src/data-display/Heading/Heading";
import type { IResource } from "@gettactic/api";
import pluralize from "pluralize";
import React from "react";
import { useReserveForm } from "@/features/reserve/components/Reserve/ReserveFormProvider";

export interface MapReserveCardMultiRoomHeaderProps {
  roomsCount: number;
  /** The room the card is showing — its floor is the one we name. */
  viewingRoom: IResource;
}

const classes = {
  Wrapper: "flex w-full flex-col gap-1",
  Title: "truncate font-bold font-inter text-[18px]",
  Subtitle: "font-inter font-semiBold text-[13px] text-muted-foreground"
};

/**
 * Header of a reservation holding several rooms: with more than one room the card is
 * about the reservation rather than about any single room, so it leads with its title.
 */
export const MapReserveCardMultiRoomHeader: React.FC<
  MapReserveCardMultiRoomHeaderProps
> = ({ roomsCount, viewingRoom }) => {
  const { form, currentOffice, floors } = useReserveForm();
  const title = form.watch("title");

  const floorName = React.useMemo(
    () => floors?.find((floor) => floor.id === viewingRoom.parent_id)?.name,
    [floors, viewingRoom.parent_id]
  );

  const subtitle = [
    `${roomsCount} ${pluralize("room", roomsCount)}`,
    currentOffice?.name,
    floorName
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <div className={classes.Wrapper}>
      <Heading as="h5" className={classes.Title}>
        {title || "Room Reservation"}
      </Heading>
      <span className={classes.Subtitle}>{subtitle}</span>
    </div>
  );
};
